import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/** Expand the viewport so autoplay media starts loading shortly before it enters view (vertical scroll). */
const DEFERRED_MEDIA_VIEWPORT_MARGIN = '200px 0px';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  #abortController = new AbortController();

  /** @type {IntersectionObserver | null} */
  #viewportObserver = null;

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.#handleMediaStartedPlaying, { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });

    // Single player in DOM: markup in <template> only; clone once when autoplay or no poster (snippets/video.liquid).
    // Wait until near viewport so off-screen autoplay does not download/decode on initial page load.
    if (this.hasAttribute('autoplay') || this.hasAttribute('data-immediate')) {
      this.#scheduleAutoplayWhenInView();
    }
  }

  disconnectedCallback() {
    this.#disconnectViewportObserver();
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Loads autoplay / immediate media once the host element intersects the viewport (with margin), or right away if IO is missing.
   */
  #scheduleAutoplayWhenInView() {
    if (this.getAttribute('data-media-loaded')) return;

    const bootstrap = () => {
      queueMicrotask(() => {
        if (this.getAttribute('data-media-loaded')) return;
        this.loadContent(false);
        if (!this.getAttribute('data-media-loaded')) return;
        this.isPlaying = true;
        this.updatePlayPauseHint(true);
      });
    };

    if (!('IntersectionObserver' in window)) {
      bootstrap();
      return;
    }

    this.#viewportObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || entry.target !== this) continue;
          this.#disconnectViewportObserver();
          bootstrap();
          return;
        }
      },
      {
        root: null,
        rootMargin: DEFERRED_MEDIA_VIEWPORT_MARGIN,
        threshold: 0,
      }
    );

    this.#viewportObserver.observe(this);
  }

  #disconnectViewportObserver() {
    if (this.#viewportObserver) {
      this.#viewportObserver.disconnect();
      this.#viewportObserver = null;
    }
  }

  /**
   * Pauses this media when another starts, except concurrent autoplay videos (e.g. UGC grids).
   * @param {MediaStartedPlayingEvent} event
   */
  #handleMediaStartedPlaying = (event) => {
    const source = event.detail?.resource;
    if (source === this) return;

    if (source?.hasAttribute('autoplay') && this.hasAttribute('autoplay')) return;

    this.pauseMedia();
  };

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    this.#disconnectViewportObserver();
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');

    if (content instanceof HTMLVideoElement && content.getAttribute('autoplay')) {
      // force autoplay for safari
      content.play();
    }
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    const iframes = this.querySelectorAll('iframe[data-video-type]');
    if (iframes.length > 0) {
      for (const iframe of iframes) {
        if (!(iframe instanceof HTMLIFrameElement)) continue;
        iframe.contentWindow?.postMessage(
          iframe.dataset.videoType === 'youtube'
            ? '{"event":"command","func":"playVideo","args":""}'
            : '{"method":"play"}',
          '*'
        );
      }
    } else {
      for (const video of this.querySelectorAll('video')) {
        video.play();
      }
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    const iframes = this.querySelectorAll('iframe[data-video-type]');

    if (iframes.length > 0) {
      for (const iframe of iframes) {
        if (!(iframe instanceof HTMLIFrameElement)) continue;
        iframe.contentWindow?.postMessage(
          iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
            : '{"method":"pause"}',
          '*'
        );
      }
    } else {
      for (const video of this.querySelectorAll('video')) {
        video.pause();
      }
    }
    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
